// navigation-completion Stage E/F — Inspector → Navigation tab.
//
// The exception/correction surface over AUTOMATED navigation preparation. It
// reports the authoritative source + current draft identity (revision, source
// and artifact hashes, policy version), protection and qualification state, then
// lets an authorised reviewer generate proposals, accept/reject them (with an
// optional manual target), and edit bookmarks/hyperlinks.
//
// Interaction model (Stage F review):
//   • Bookmarks / Hyperlinks / Proposals are INNER TABS. The editor subtrees are
//     kept MOUNTED (display-toggled, never remounted) once shown, so a reviewer's
//     unsaved inputs survive switching tabs or expanding the workspace. Hidden
//     subtrees are flagged (data-nav-hidden) and excluded from the modal focus
//     trap.
//   • The reason prompt appears at ACTION/SAVE time — a small contextual
//     confirmation area that NAMES the intended change — not an always-visible
//     textarea above the lists. Every mutation carries a non-empty reason and the
//     exact revision/hash/policy-version the reviewer saw (compare-and-swap).
//     Those CAS tokens are SNAPSHOTTED WHEN THE ACTION IS STAGED, never re-read
//     from a later envelope; a 409 asks the reviewer to refresh and re-stage
//     rather than silently rebinding the staged edit to new revision tokens.
//
// Stage F — SIDE-BY-SIDE workspace: the editors sit beside a real pdf.js canvas
// preview of the EXACT revision bytes (via the shared inline-view hook). A
// reviewer can highlight a hyperlink SOURCE rectangle or follow a bookmark/link
// DESTINATION right in the preview, and expand the whole workspace to a focused
// full-viewport dialog WITHOUT losing unsaved editor state.
//
// phase-compiler-six-tabs — this component now renders inside the six-tab
// workspace's Bookmarks & Links panel (the old Inspector is retired). The
// policy snapshot control is co-located ABOVE this tab on that same panel;
// Release/QC and the regional-criteria assessment live on the Validation tab.
// This tab is only the bookmark/hyperlink editing surface + its preview.

import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { ApiError, parseApiErrorBody } from '@/api/v2Client';
import {
  documentNavigation,
  type EditTarget,
  type ExpectedTokens,
  type NavEdit,
  type NavigationEnvelope,
} from '@/api/documentNavigation';
import { useInlineDocumentView } from '@/hooks/useInlineDocumentView';
import { useSubmissionDraft } from '@/hooks/useSubmissionWorkspace';
import { useElementWidth } from '@/hooks/useElementWidth';
import { tabKeyboard } from '@/lib/tabKeyboard';
import { navStyles } from './navigation/navShared';
import { NavigationProposals } from './navigation/NavigationProposals';
import { BookmarkEditor } from './navigation/BookmarkEditor';
import { HyperlinkEditor } from './navigation/HyperlinkEditor';
import { PdfCanvasViewer, type PdfPreviewFocus, type PdfSelectionRequest, type PdfSelectionResult, type SelectionKind } from './navigation/PdfCanvasViewer';
import { useNavigationEnvelope } from './navigation/useNavigationEnvelope';
import { useRemoteNavigationPreview } from './navigation/useRemoteNavigationPreview';
import { DocumentBadge } from '../DocumentBadge';
import type { LifecycleOperation } from '../lifecycle';

export interface NavigationTabProps {
  readonly documentId: string;
  readonly fileName: string | null;
  /** The document's assigned lifecycle operation, rendered as a canonical badge
   *  beside the filename (including inside the expanded workspace overlay). */
  readonly operation?: LifecycleOperation | string | null;
  /** Documents:write — the client-side gate; the server re-checks every call. */
  readonly canEdit: boolean;
  /** Whether this is the currently-shown workspace tab. The parent keeps the
   *  subtree MOUNTED while hidden (so drafts survive a main-tab switch); a
   *  false→true re-entry re-reads the envelope so changes made while hidden are
   *  seen, while any FROZEN staged tokens stay frozen. Defaults to true for
   *  stand-alone use. */
  readonly active?: boolean;
  /** Switch the workspace to the link-validation (Validation tab) inventory. */
  readonly onShowLinks?: () => void;
  /**
   * phase-compiler-six-tabs — bumped by the parent after an in-tab action that
   * changes the envelope but does NOT unmount this subtree (e.g. applying the
   * submission policy snapshot from the control co-located on the Bookmarks &
   * Links tab). Each change re-reads the envelope so the editor unlocks /
   * updates in place; FROZEN staged tokens stay frozen because they live on
   * `staged`, not `env`, so drafts are never discarded by a refresh.
   */
  readonly refreshNonce?: number;
  /**
   * phase-compiler-six-tabs (layout correction) — true when this tab is rendered
   * INSIDE the six-tab Bookmarks & Links panel, where the shared selected-file
   * header already shows the filename + lifecycle badge. In that case we drop the
   * duplicate filename from the "Navigation review" heading and the inline
   * "Editing workspace" toolbar so the normal view stays concise. The EXPANDED
   * full-viewport workspace still shows the filename + badge (the shared header
   * is not visible under the overlay). Defaults to false so the legacy Inspector
   * host (DocumentInspector) keeps its original self-identifying header.
   */
  readonly embedded?: boolean;
}

type InnerTab = 'proposals' | 'bookmarks' | 'hyperlinks';

/** The CAS tokens an action froze when it was STAGED (not re-read later). */
interface StagedTokens {
  readonly expectedRevision: number;
  readonly expectedArtifactSha256: string;
  readonly expectedPolicyVersion: number;
}

/** A mutation the reviewer has initiated but not yet confirmed with a reason. */
interface StagedAction {
  readonly key: string;
  /** Human sentence naming the exact change (shown in the confirmation area). */
  readonly label: string;
  /** CAS tokens snapshotted at stage time. */
  readonly tokens: StagedTokens;
  readonly run: (tokens: ExpectedTokens) => Promise<NavigationEnvelope>;
}

function shortSha(sha: string): string {
  return sha.length > 12 ? `${sha.slice(0, 12)}…` : sha;
}

/** Name the staged edit so the confirmation area states what is about to change. */
function describeEdit(edit: NavEdit): string {
  switch (edit.op) {
    case 'createBookmark':
      return `Add the bookmark “${edit.title}”`;
    case 'renameBookmark':
      return `Rename this bookmark to “${edit.title}”`;
    case 'removeBookmark':
      return 'Remove this bookmark';
    case 'reorderBookmark':
      return 'Reorder this bookmark among its siblings';
    case 'indentBookmark':
      return 'Indent this bookmark';
    case 'outdentBookmark':
      return 'Outdent this bookmark';
    case 'retargetBookmark':
      return 'Change this bookmark’s target';
    case 'createHyperlink':
      return `Add a hyperlink on page ${edit.page}`;
    case 'removeHyperlink':
      return 'Remove this hyperlink';
    case 'retargetHyperlink':
      return 'Change this hyperlink’s target';
    case 'styleHyperlink':
      return 'Restyle this hyperlink';
    case 'moveHyperlink':
      return 'Change this hyperlink’s source area';
    default: {
      const _never: never = edit;
      return String(_never);
    }
  }
}

const INNER_TABS: ReadonlyArray<{ key: InnerTab; label: string }> = [
  { key: 'proposals', label: 'Proposals' },
  { key: 'bookmarks', label: 'Bookmarks' },
  { key: 'hyperlinks', label: 'Hyperlinks' },
];

export function NavigationTab({ documentId, fileName, operation, canEdit, active = true, onShowLinks, refreshNonce = 0, embedded = false }: NavigationTabProps): ReactElement {
  const { env, loading, loadError, refetch, setEnv } = useNavigationEnvelope(documentId);
  const [inner, setInner] = useState<InnerTab>('proposals');
  const [staged, setStaged] = useState<StagedAction | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  // Each inner editor reports whether it holds unsaved input (an open row form or
  // the add form). Aggregated below so the whole workspace registers as draft
  // state, not only a staged reason.
  const [proposalsDirty, setProposalsDirty] = useState(false);
  const [bookmarksDirty, setBookmarksDirty] = useState(false);
  const [hyperlinksDirty, setHyperlinksDirty] = useState(false);
  // Bumped to force a one-shot remount of the editor subtree when drafts are
  // discarded (the workspace asked us to) — combined with the envelope identity
  // key below so saving/refetching to a NEW revision also resets stale inputs.
  const [discardNonce, setDiscardNonce] = useState(0);
  // Explains that unsaved editor inputs were cleared because the draft advanced
  // to a new identity (so an old handle is never rebound to new CAS tokens).
  const [identityNotice, setIdentityNotice] = useState<string | null>(null);

  // Preview focus (a jump/highlight request from an editor row). Each request
  // carries a bumped nonce so clicking the same affordance twice re-applies it.
  const [focus, setFocus] = useState<PdfPreviewFocus | null>(null);
  const nonceRef = useRef(0);
  // Visual selection channel: the active editor arms a source/destination
  // selection and supplies a result handler; the viewer resolves the gesture to
  // PDF geometry and we route it back to that handler. The request identity's
  // nonce lets the same kind be re-armed after a cancel.
  const [selectionReq, setSelectionReq] = useState<PdfSelectionRequest | null>(null);
  const selectNonceRef = useRef(0);
  // The armed selection carries the viewer SURFACE (the exact bytes URL) it was
  // armed against — the source revision, or a specific cross-document preview. A
  // commit is delivered to its callback ONLY while that same surface is still
  // shown; if the preview is returned/switched (so the viewer now shows a
  // different document) the request is disarmed and never applied, so a gesture
  // on the source document can never be staged as a remote destination (Kimi F1).
  const pendingSelectRef = useRef<{ onResult: (r: PdfSelectionResult) => void; armedUrl: string | null } | null>(null);
  // A cross-document target preview: the picker hands us the exact bytes, we own
  // the object URL and render it in the SAME viewer with a "return" affordance.
  // The hook owns exact-byte URLs and scopes cleanup to the originating picker.
  const { preview: remotePreview, onPreview: onRemotePreview, clearPreview: clearRemotePreview } =
    useRemoteNavigationPreview(documentId);
  // Bumped whenever the cross-document preview is intentionally cleared/returned
  // (return button, a local focus preview, a document switch). An in-flight saved-
  // remote "View destination" fetch in the editor reads this and drops its result
  // if it changed, so a late completion never revives a preview the reviewer left
  // (saved-target review item 3: return-to-source before the fetch completes).
  const [remotePreviewNonce, setRemotePreviewNonce] = useState(0);
  const returnToSource = useCallback(() => {
    setRemotePreviewNonce((n) => n + 1);
    clearRemotePreview();
  }, [clearRemotePreview]);
  const [expanded, setExpanded] = useState(false);
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Lay the editor BESIDE the preview only when the workspace CONTAINER is wide
  // enough for two usable columns. A pinned pane can be narrow on a wide desktop,
  // so the decision measures this element, not the viewport (the canonical
  // container-width hook, as used by the PDF/A compliance panel). The composed
  // ref keeps the imperative `workspaceRef` (focus trap / query) working while
  // the hook observes the same node — the node is never remounted on resize, so
  // the editors and viewer keep their draft / focus / revision state.
  const { ref: measureWorkspace, width: workspaceWidth } = useElementWidth<HTMLDivElement>();
  const setWorkspaceEl = useCallback(
    (node: HTMLDivElement | null) => {
      workspaceRef.current = node;
      measureWorkspace(node);
    },
    [measureWorkspace],
  );

  // A staged-but-unconfirmed change, a confirmation in flight, OR an open editor
  // draft (a row rename/retarget/style or an add form with unsaved input) is all
  // unsaved work — register it so another editor's refresh cannot silently
  // discard it. The discard callback clears the staged action AND remounts the
  // editor subtree to drop its internal inputs.
  const anyEditorDirty = proposalsDirty || bookmarksDirty || hyperlinksDirty;
  const anyEditorDirtyRef = useRef(anyEditorDirty);
  anyEditorDirtyRef.current = anyEditorDirty;
  const discardAll = useCallback(() => {
    setStaged(null);
    setReason('');
    setActionError(null);
    setConflict(null);
    setProposalsDirty(false);
    setBookmarksDirty(false);
    setHyperlinksDirty(false);
    setDiscardNonce((n) => n + 1);
  }, []);
  useSubmissionDraft(staged !== null || anyEditorDirty, discardAll, busy !== null);

  // Reset per-document UI state when the selected document changes (the envelope
  // hook resets its own state; these are this tab's local concerns).
  useEffect(() => {
    setStaged(null);
    setReason('');
    setActionError(null);
    setConflict(null);
    setFocus(null);
    setExpanded(false);
    setInner('proposals');
    setIdentityNotice(null);
    setSelectionReq(null);
    pendingSelectRef.current = null;
    setRemotePreviewNonce((n) => n + 1);
    clearRemotePreview();
  }, [documentId, clearRemotePreview]);

  // When the workspace hides this tab (a main-tab switch keeps us MOUNTED), fold
  // the expanded overlay so its body-scroll lock is released; unsaved editor and
  // staged state is untouched.
  useEffect(() => {
    if (!active) setExpanded(false);
  }, [active]);

  // Envelope-refresh requests are QUEUED, never consumed on arrival. Two things
  // request a refresh:
  //   • RE-ENTRY  — a false→true `active` transition (a snapshot apply or a
  //     document change made while we were hidden must be seen on return).
  //   • SNAPSHOT  — the co-located NavigationSnapshotControls bumps refreshNonce
  //     after applying the submission policy snapshot (we are NOT unmounted).
  // A refresh may only RUN when the tab is active AND idle (busy === null): a
  // refetch mid-save would swap the envelope under an in-flight confirm. If a
  // trigger arrives while hidden or busy we remember it in `pendingRefresh` and
  // run it once both conditions hold — so neither trigger is ever lost (the old
  // code updated its guards' refs before the active/busy check and dropped a
  // refresh that landed during a save). FROZEN staged tokens are never rebound:
  // they live on `staged`, and refetch only replaces `env`.
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const wasActive = useRef(active);
  useEffect(() => {
    if (active && !wasActive.current) setPendingRefresh(true);
    wasActive.current = active;
  }, [active]);
  const lastRefreshNonce = useRef(refreshNonce);
  useEffect(() => {
    if (refreshNonce !== lastRefreshNonce.current) {
      lastRefreshNonce.current = refreshNonce;
      setPendingRefresh(true);
    }
  }, [refreshNonce]);
  useEffect(() => {
    if (pendingRefresh && active && busy === null) {
      setPendingRefresh(false);
      void refetch();
    }
  }, [pendingRefresh, active, busy, refetch]);

  const revision = env?.revision ?? 0;
  const isPdf = env != null && env.inventory != null;
  const preview = useInlineDocumentView(documentId, {
    enabled: isPdf,
    loader: () => documentNavigation.file(documentId, revision),
    cacheKey: `nav:${documentId}:rev${revision}`,
  });

  const focusPreview = useCallback((next: PdfPreviewFocus) => {
    nonceRef.current += 1;
    // A fresh in-document focus returns the viewer to this revision's bytes.
    setRemotePreviewNonce((n) => n + 1);
    clearRemotePreview();
    setFocus({ ...next, nonce: nonceRef.current });
  }, [clearRemotePreview]);
  const onJumpToPage = useCallback((page1: number) => focusPreview({ page: page1 }), [focusPreview]);

  // Arm a visual selection for the editor that asked. By default it runs against
  // THIS document's own bytes, so any cross-document preview is returned first.
  // `opts.keepPreview` leaves the current cross-document preview in place so the
  // reviewer can draw a landing point on the AUTHORIZED TARGET's own page (the
  // picker's exact-bytes preview). A new request supersedes any prior armed one.
  const beginSelection = useCallback(
    (kind: SelectionKind, onResult: (r: PdfSelectionResult) => void, opts?: { keepPreview?: boolean }) => {
      // The surface this selection is bound to: a keepPreview (remote landing)
      // selection is drawn on the CURRENT cross-document preview; otherwise it is
      // drawn on THIS document's own bytes, so any preview is returned first.
      const armedUrl = opts?.keepPreview ? (remotePreview?.url ?? null) : (preview.url ?? null);
      if (!opts?.keepPreview) clearRemotePreview();
      pendingSelectRef.current = { onResult, armedUrl };
      selectNonceRef.current += 1;
      setSelectionReq({ kind, nonce: selectNonceRef.current });
    },
    [clearRemotePreview, remotePreview, preview.url],
  );
  const handleViewerSelect = useCallback((r: PdfSelectionResult) => {
    const pending = pendingSelectRef.current;
    pendingSelectRef.current = null;
    setSelectionReq(null);
    if (!pending) return;
    // Deliver ONLY when the viewer still shows the surface the gesture was armed
    // against — otherwise the preview changed under the gesture and the geometry
    // belongs to a different document; drop it rather than cross-apply it.
    const currentUrl = remotePreview ? remotePreview.url : (preview.url ?? null);
    if (pending.armedUrl !== currentUrl) return;
    pending.onResult(r);
  }, [remotePreview, preview.url]);
  const handleViewerSelectCancel = useCallback(() => {
    pendingSelectRef.current = null;
    setSelectionReq(null);
  }, []);

  // Disarm an armed selection the moment the viewer surface changes away from the
  // one it was armed against (e.g. "Return to revision N", a different target
  // radio, a local focus preview) — so the selection bar never stays armed over a
  // document the request was not meant for (Kimi F1).
  useEffect(() => {
    const pending = pendingSelectRef.current;
    if (!pending) return;
    const currentUrl = remotePreview ? remotePreview.url : (preview.url ?? null);
    if (pending.armedUrl !== currentUrl) {
      pendingSelectRef.current = null;
      setSelectionReq(null);
    }
  }, [remotePreview, preview.url]);

  // ---- staging: capture the change + its CAS tokens NOW, prompt for a reason -
  const stage = useCallback(
    (key: string, label: string, run: (tokens: ExpectedTokens) => Promise<NavigationEnvelope>) => {
      if (!env) return;
      setActionError(null);
      setConflict(null);
      setIdentityNotice(null);
      setStaged({
        key,
        label,
        run,
        // Frozen at stage time — a later refresh never rebinds these.
        tokens: {
          expectedRevision: env.revision,
          expectedArtifactSha256: env.artifactSha256,
          expectedPolicyVersion: env.policyVersion,
        },
      });
    },
    [env],
  );

  const cancelStaged = useCallback(() => {
    setStaged(null);
    setReason('');
    setActionError(null);
    setConflict(null);
  }, []);

  const confirmStaged = useCallback(async (): Promise<void> => {
    if (!staged) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setActionError('Enter a reason for this change before saving.');
      return;
    }
    setBusy(staged.key);
    setActionError(null);
    setConflict(null);
    try {
      // Whether OTHER editors held unsaved input at save time — the new revision
      // below changes the editor identity and so clears them; say so plainly.
      const hadOtherDrafts = anyEditorDirtyRef.current;
      const next = await staged.run({ ...staged.tokens, reason: trimmed });
      setEnv(next);
      setStaged(null);
      setReason('');
      setIdentityNotice(
        hadOtherDrafts
          ? 'Saved. The draft advanced to the latest revision, so unsaved edits elsewhere in the editors ' +
              'were cleared rather than reapplied against the new revision — reopen them to redo.'
          : null,
      );
    } catch (err) {
      const body = parseApiErrorBody(err);
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 409) {
        // Keep the staged change + reason visible (nothing was retried), but its
        // tokens are now stale: the reviewer must refresh and re-stage.
        setConflict(
          `${body?.message ?? 'The navigation draft changed since you loaded it.'} ` +
            'Refresh to load the latest revision, then reapply your change — nothing was retried ' +
            'automatically, and the staged change is NOT rebound to the new revision.',
        );
      } else {
        // Keep the staged change + reason so the reviewer can retry/adjust.
        setActionError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
      }
    } finally {
      setBusy(null);
    }
  }, [staged, reason, setEnv]);

  // A refresh after a conflict discards the stale staged change (no silent
  // rebind) and reloads the latest revision; the reviewer re-initiates the edit.
  const onRefresh = useCallback(() => {
    setStaged(null);
    setReason('');
    setConflict(null);
    setActionError(null);
    setIdentityNotice(null);
    void refetch();
  }, [refetch]);

  const onEdit = useCallback(
    (edit: NavEdit, actionKey: string) =>
      stage(actionKey, describeEdit(edit), (tokens) =>
        documentNavigation.edit(documentId, { ...tokens, edits: [edit] }),
      ),
    [documentId, stage],
  );
  const onDispose = useCallback(
    (proposalId: string, decision: 'accept' | 'reject', manualTarget: EditTarget | undefined, actionKey: string) =>
      stage(
        actionKey,
        decision === 'reject'
          ? 'Reject this proposal'
          : manualTarget !== undefined
            ? 'Accept this proposal with the target you chose'
            : 'Accept this proposal',
        (tokens) =>
          documentNavigation.dispose(documentId, proposalId, {
            ...tokens,
            decision,
            ...(manualTarget !== undefined ? { manualTarget } : {}),
          }),
      ),
    [documentId, stage],
  );
  const onGenerate = useCallback(
    () => stage('generate', 'Generate missing navigation from the source', (tokens) => documentNavigation.generate(documentId, tokens)),
    [documentId, stage],
  );
  const onRevert = useCallback(
    () => stage('revert', 'Revert the draft to the authoritative source', (tokens) => documentNavigation.revert(documentId, tokens)),
    [documentId, stage],
  );

  // ---- expanded (full-viewport) workspace: Escape to close, focus trap +
  // restore, body scroll lock. The SAME subtree is re-styled below — never
  // remounted — so unsaved editor inputs survive expand/collapse. A hidden inner
  // tab's subtree is flagged data-nav-hidden and excluded from the trap. --------
  useEffect(() => {
    if (!expanded) return;
    restoreFocusRef.current = (globalThis.document.activeElement as HTMLElement | null) ?? null;
    const el = workspaceRef.current;
    const collapseBtn = el?.querySelector<HTMLElement>('[data-testid="nav-workspace-collapse"]');
    (collapseBtn ?? el)?.focus();
    const previousOverflow = globalThis.document.body.style.overflow;
    globalThis.document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setExpanded(false);
        return;
      }
      if (e.key === 'Tab' && el) {
        const focusables = Array.from(
          el.querySelectorAll<HTMLElement>(
            'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
          ),
        ).filter((node) => node.closest('[data-nav-hidden="true"]') === null);
        if (focusables.length === 0) return;
        const first = focusables[0] as HTMLElement;
        const last = focusables[focusables.length - 1] as HTMLElement;
        const active = globalThis.document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    globalThis.document.addEventListener('keydown', onKey, true);
    return () => {
      globalThis.document.removeEventListener('keydown', onKey, true);
      globalThis.document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus?.();
    };
  }, [expanded]);

  const linksAffordance = onShowLinks && (
    <button type="button" style={navStyles.secondaryBtn} data-testid="nav-open-links" onClick={onShowLinks}>
      Open link-validation inventory
    </button>
  );

  if (loading && !env) {
    return (
      <div style={navStyles.muted} data-testid="nav-tab-loading">
        Loading navigation review…
      </div>
    );
  }
  if (loadError && !env) {
    return (
      <div style={navStyles.error} role="alert" data-testid="nav-tab-load-error">
        Could not load navigation review: {loadError}
      </div>
    );
  }
  if (!env) return <div data-testid="nav-tab-empty" style={navStyles.muted} />;

  const canMutate = canEdit && isPdf && env.protection.mutationAllowedByState;
  const mutationBlockedReason = !canEdit
    ? 'you do not have document write permission'
    : !env.protection.mutationAllowedByState
      ? env.protection.detail
      : !isPdf
        ? 'the artifact is not an editable PDF'
        : null;
  // Editors lock while a change is staged (awaiting a reason) or a mutation is in
  // flight, so a second action cannot race the first.
  const editorsBusy = busy !== null || staged !== null;

  // The exact draft identity: revision + artifact + policy (plus a discard nonce).
  // Used to KEY the editor subtree so a save/refetch that advances to a new
  // revision REMOUNTS the editors, dropping stale inputs — an old row handle can
  // never be rebound to new CAS tokens. It deliberately excludes the inner tab
  // and expanded flag, so switching tabs or expanding preserves same-identity
  // inputs (the subtree is not remounted).
  const editorIdentity = `${env.revision}:${env.artifactSha256}:${env.policyVersion}:${discardNonce}`;

  const viewerUrl = remotePreview ? remotePreview.url : preview.url;
  const viewerCaption = remotePreview
    ? `Target preview — ${remotePreview.label}`
    : `Revision ${env.revision}`;

  // Stack the editor above the preview when the workspace is too narrow for two
  // usable columns (a quarter-width pinned pane). The measured element is the
  // overlay itself when expanded, so a NARROW viewport (a phone/narrow window)
  // under the expanded modal stacks too — the old `!expanded` guard wrongly
  // forced a wide desktop assumption and left two ~185px columns inside a ~390px
  // modal. An unmeasured width (null) defaults to side-by-side so the narrow
  // layout never flashes before measuring.
  const stacked = workspaceWidth !== null && workspaceWidth < NAV_SIDE_BY_SIDE_MIN_WIDTH;

  const renderEditor = (key: InnerTab, node: ReactElement): ReactElement => {
    const active = inner === key;
    return (
      <div
        key={key}
        id={`nav-inner-panel-${key}`}
        role="tabpanel"
        aria-labelledby={`nav-inner-${key}`}
        data-testid={`nav-inner-panel-${key}`}
        data-nav-hidden={active ? undefined : 'true'}
        {...(active ? {} : { 'aria-hidden': true })}
        style={{ display: active ? 'block' : 'none' }}
      >
        {node}
      </div>
    );
  };

  return (
    <div data-testid="nav-tab">
      {/* -------- identity / state header (compact) -------- */}
      <section style={navStyles.section} data-testid="nav-header">
        <h4 style={navStyles.heading}>Navigation review{!embedded && fileName ? ` — ${fileName}` : ''}</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px' }}>
          <span style={navStyles.label}>Revision</span>
          <span style={navStyles.value} data-testid="nav-revision">
            {env.revision === 0 ? '0 (authoritative source — no draft yet)' : env.revision}
          </span>
          <span style={navStyles.label}>Protection</span>
          <span style={navStyles.value} data-testid="nav-protection">
            {env.protection.mutationAllowedByState ? 'Editable' : 'Locked'} — {env.protection.detail}
          </span>
        </div>

        <div
          style={{ ...navStyles.note, color: env.qualification.eligible ? 'var(--ok, #15803d)' : 'var(--warning, #b45309)' }}
          data-testid="nav-qualification"
        >
          {env.qualification.eligible
            ? 'Qualified for release.'
            : 'Review only — qualification pending. Drafts can be reviewed but are NOT release or shipping eligible yet.'}
        </div>

        <details style={{ marginTop: 8 }} data-testid="nav-identity-details">
          <summary style={{ ...navStyles.label, cursor: 'pointer' }}>Technical details</summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px', marginTop: 6 }}>
            {env.draftId && (
              <>
                <span style={navStyles.label}>Draft</span>
                <span style={{ ...navStyles.value, ...navStyles.mono }} data-testid="nav-draft-id">{shortSha(env.draftId)}</span>
              </>
            )}
            <span style={navStyles.label}>Source hash</span>
            <span style={{ ...navStyles.value, ...navStyles.mono }} data-testid="nav-source-hash">{shortSha(env.sourceSha256)}</span>
            <span style={navStyles.label}>Artifact hash</span>
            <span style={{ ...navStyles.value, ...navStyles.mono }} data-testid="nav-artifact-hash">{shortSha(env.artifactSha256)}</span>
            <span style={navStyles.label}>Policy version</span>
            <span style={navStyles.value} data-testid="nav-policy-version">{env.policyVersion}</span>
            <span style={navStyles.label}>Qualification detail</span>
            <span style={navStyles.value} data-testid="nav-qualification-detail">{env.qualification.reason}</span>
          </div>
          <div style={{ ...navStyles.muted, marginTop: 6 }} data-testid="nav-provenance-legend">
            Provenance: existing navigation is preserved; generated proposals are reviewed; manual
            edits and manual targets are recorded as reviewer provenance.
          </div>
        </details>

        {mutationBlockedReason && (
          <div style={{ ...navStyles.note, color: 'var(--text-muted)' }} data-testid="nav-mutation-blocked">
            Editing is disabled: {mutationBlockedReason}.{' '}
            {!env.protection.mutationAllowedByState && env.protection.state !== 'verified-unsigned'
              ? 'A signed, protected or unverified artifact is not rewritten; use an authorised external-correction or re-signing workflow.'
              : ''}
          </div>
        )}

        {isPdf && env.snapshotAuthorizesMutation === false && (
          <div style={{ ...navStyles.note, color: 'var(--warning, #b45309)' }} data-testid="nav-snapshot-warning">
            Changes are not authorised yet: this submission has no current, activated policy snapshot, so
            byte-changing edits will be refused at save. Apply the submission policy snapshot from the
            control at the top of this Bookmarks &amp; Links tab, then return here to edit.
          </div>
        )}
        {env.orphanedOverrides && env.orphanedOverrides.length > 0 && (
          <div style={{ ...navStyles.note, color: 'var(--warning, #b45309)' }} role="alert" data-testid="nav-orphaned-overrides">
            {env.orphanedOverrides.length} earlier reviewer decision
            {env.orphanedOverrides.length === 1 ? '' : 's'} no longer match the current document and{' '}
            {env.orphanedOverrides.length === 1 ? 'was' : 'were'} not reapplied automatically — revisit{' '}
            {env.orphanedOverrides.length === 1 ? 'it' : 'them'}:
            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
              {env.orphanedOverrides.map((o) => (
                <li key={o.slotKey}>
                  <span style={navStyles.mono}>{o.slotKey}</span> — {o.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {env.corruptOverrides && env.corruptOverrides.length > 0 && (
          <div style={{ ...navStyles.note, color: 'var(--danger, #b91c1c)' }} role="alert" data-testid="nav-corrupt-overrides">
            {env.corruptOverrides.length} stored reviewer decision
            {env.corruptOverrides.length === 1 ? '' : 's'} could not be decoded and {env.corruptOverrides.length === 1 ? 'was' : 'were'}{' '}
            not applied — re-make {env.corruptOverrides.length === 1 ? 'it' : 'them'}:
            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
              {env.corruptOverrides.map((o) => (
                <li key={o.slotKey}>
                  <span style={navStyles.mono}>{o.slotKey}</span> — {o.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>{linksAffordance}</div>
      </section>

      {/* -------- non-PDF (Word-native) unsupported path -------- */}
      {env.unsupportedReason && (
        <section style={navStyles.section} data-testid="nav-unsupported">
          <h4 style={navStyles.heading}>PDF navigation editing is not available</h4>
          <div style={navStyles.muted}>{env.unsupportedReason}</div>
          <div style={{ ...navStyles.note }}>
            The existing native validation for this artifact is unchanged. Correct the navigation in
            the source application (or upload an approved PDF rendition) — this PDF editor does not
            edit the Word artifact that will ship.
          </div>
        </section>
      )}

      {/* -------- side-by-side editing workspace -------- */}
      {isPdf && env.inventory && (
        <div
          ref={setWorkspaceEl}
          data-testid="nav-workspace"
          style={expanded ? workspaceOverlay : workspaceInline}
          // When EXPANDED this is a long-lived role=dialog. It manages its own
          // unsaved-draft state via useSubmissionDraft (staged / anyEditorDirty),
          // so mark it managed-draft: the workspace's changed-dialog fallback must
          // not keep the unsaved-changes warning alive after the draft is cancelled
          // while this container stays open (saved-target review item 4).
          {...(expanded ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Navigation editing workspace', 'data-managed-draft': 'true' } : {})}
        >
          <div style={workspaceToolbar}>
            {/* Keep the document context visible even under the full-viewport
                overlay: the filename and its actual lifecycle badge (the same
                canonical DocumentBadge the shared selected-file header uses). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }} data-testid="nav-workspace-identity">
              {/* The six-tab panel (embedded) already frames this surface with the
                  shared selected-file header and the tab itself, so the decorative
                  "Editing workspace" label is redundant chrome there — show it only
                  standalone or under the full-viewport overlay (no shared header). */}
              {(!embedded || expanded) && <span style={navStyles.label}>Editing workspace</span>}
              {/* The filename + lifecycle badge are duplicated by the shared
                  selected-file header in the embedded (six-tab) normal view, so
                  show them here only when standalone OR when expanded (the shared
                  header is hidden under the full-viewport overlay). */}
              {(!embedded || expanded) && fileName && (
                <span style={workspaceFilename} title={fileName} data-testid="nav-workspace-filename">
                  {fileName}
                </span>
              )}
              {(!embedded || expanded) && <DocumentBadge operation={operation ?? null} size="sm" />}
            </div>
            <div style={navStyles.toolbar} data-testid="nav-actions">
              <button
                type="button"
                data-testid="nav-generate"
                disabled={!canMutate || editorsBusy}
                aria-disabled={!canMutate || editorsBusy}
                title={!canMutate && mutationBlockedReason ? mutationBlockedReason : undefined}
                style={!canMutate || editorsBusy ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
                onClick={onGenerate}
              >
                Generate missing navigation
              </button>
              <button
                type="button"
                data-testid="nav-revert"
                disabled={!canMutate || editorsBusy || env.revision === 0}
                aria-disabled={!canMutate || editorsBusy || env.revision === 0}
                title={
                  env.revision === 0
                    ? 'there is no draft to revert'
                    : !canMutate && mutationBlockedReason
                      ? mutationBlockedReason
                      : undefined
                }
                style={!canMutate || editorsBusy || env.revision === 0 ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
                onClick={onRevert}
              >
                Revert draft to source
              </button>
              {expanded ? (
                <button type="button" style={navStyles.secondaryBtn} data-testid="nav-workspace-collapse" onClick={() => setExpanded(false)}>
                  Close expanded view
                </button>
              ) : (
                <button type="button" style={navStyles.secondaryBtn} data-testid="nav-workspace-expand" onClick={() => setExpanded(true)}>
                  Expand workspace
                </button>
              )}
            </div>
          </div>

          {identityNotice && (
            <div style={{ ...navStyles.note, color: 'var(--warning, #b45309)' }} role="status" data-testid="nav-identity-notice">
              {identityNotice}
              <button type="button" style={{ ...navStyles.secondaryBtn, marginLeft: 8 }} data-testid="nav-identity-notice-dismiss" onClick={() => setIdentityNotice(null)}>
                Dismiss
              </button>
            </div>
          )}

          {/* -------- contextual save-time confirmation (names the change) ----- */}
          {staged && (
            <section style={{ ...navStyles.section, border: '1px solid var(--accent, #4f46e5)' }} data-testid="nav-confirm">
              <div style={navStyles.label} data-testid="nav-confirm-label">
                Confirm change: {staged.label}
              </div>
              <div style={{ ...navStyles.muted, marginTop: 2 }}>
                Applied against revision {staged.tokens.expectedRevision} (the version you are viewing). A
                non-empty reason is recorded in the audit trail.
              </div>
              <label style={{ ...navStyles.label, display: 'block', marginTop: 6 }} htmlFor="nav-reason-input">
                Reason (required)
              </label>
              <textarea
                id="nav-reason-input"
                data-testid="nav-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                autoFocus
                placeholder="Why this change is being made (recorded in the audit trail)"
                style={{ ...navStyles.input, width: '100%', marginTop: 4, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  data-testid="nav-confirm-save"
                  disabled={busy !== null}
                  aria-disabled={busy !== null}
                  style={busy !== null ? navStyles.buttonDisabled : navStyles.button}
                  onClick={() => void confirmStaged()}
                >
                  {busy !== null ? 'Saving…' : 'Save change'}
                </button>
                <button
                  type="button"
                  data-testid="nav-confirm-cancel"
                  disabled={busy !== null}
                  style={navStyles.secondaryBtn}
                  onClick={cancelStaged}
                >
                  Cancel
                </button>
              </div>
              {conflict && (
                <div style={{ ...navStyles.note, color: 'var(--danger, #b91c1c)' }} role="alert" data-testid="nav-conflict">
                  {conflict}
                  <div style={{ marginTop: 6 }}>
                    <button type="button" style={navStyles.secondaryBtn} data-testid="nav-refresh" onClick={onRefresh}>
                      Refresh
                    </button>
                  </div>
                </div>
              )}
              {actionError && (
                <div style={{ ...navStyles.error, marginTop: 6 }} role="alert" data-testid="nav-action-error">
                  {actionError}
                </div>
              )}
            </section>
          )}

          <div
            data-testid="nav-columns"
            data-nav-layout={stacked ? 'stacked' : 'side-by-side'}
            style={expanded ? (stacked ? oneColumnExpanded : twoColumnExpanded) : stacked ? oneColumn : twoColumn}
          >
            {/* ---- left: inner-tabbed editors (mounted-hidden) ---- */}
            <div key={editorIdentity} style={editorColumn} data-testid="nav-editor-column">
              <div role="tablist" aria-label="Navigation editors" onKeyDown={tabKeyboard} style={navStyles.innerTabBar}>
                {INNER_TABS.map((t) => {
                  const active = inner === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      id={`nav-inner-${t.key}`}
                      aria-selected={active}
                      aria-controls={`nav-inner-panel-${t.key}`}
                      tabIndex={active ? 0 : -1}
                      data-testid={`nav-inner-${t.key}`}
                      onClick={() => setInner(t.key)}
                      style={{ ...navStyles.innerTab, ...(active ? navStyles.innerTabActive : null) }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {renderEditor(
                'proposals',
                <NavigationProposals
                  proposals={env.proposals}
                  canMutate={canMutate}
                  mutationBlockedReason={mutationBlockedReason}
                  decisions={env.decisions}
                  pageCount={env.inventory.pageCount}
                  busy={editorsBusy}
                  onDispose={onDispose}
                  onJumpToPage={onJumpToPage}
                  onPreviewFocus={focusPreview}
                  onPreview={onRemotePreview}
                  documentId={documentId}
                  onDirtyChange={setProposalsDirty}
                />,
              )}
              {renderEditor(
                'bookmarks',
                <BookmarkEditor
                  bookmarks={env.inventory.bookmarks}
                  outlineIncomplete={env.inventory.outlineIncomplete}
                  pageCount={env.inventory.pageCount}
                  canMutate={canMutate}
                  mutationBlockedReason={mutationBlockedReason}
                  decisions={env.decisions}
                  busy={editorsBusy}
                  onEdit={onEdit}
                  onJumpToPage={onJumpToPage}
                  onPreviewFocus={focusPreview}
                  onPreview={onRemotePreview}
                  documentId={documentId}
                  onDirtyChange={setBookmarksDirty}
                />,
              )}
              {renderEditor(
                'hyperlinks',
                <HyperlinkEditor
                  links={env.inventory.links}
                  annotationIncomplete={env.inventory.annotationIncomplete}
                  pageCount={env.inventory.pageCount}
                  canMutate={canMutate}
                  mutationBlockedReason={mutationBlockedReason}
                  decisions={env.decisions}
                  busy={editorsBusy}
                  onEdit={onEdit}
                  onJumpToPage={onJumpToPage}
                  onPreviewFocus={focusPreview}
                  onPreview={onRemotePreview}
                  documentId={documentId}
                  onDirtyChange={setHyperlinksDirty}
                  onBeginSelect={beginSelection}
                  remotePreviewNonce={remotePreviewNonce}
                />,
              )}
            </div>

            {/* ---- right: live PDF preview of the exact revision bytes ---- */}
            <div style={stacked ? viewerColumnStacked : viewerColumn} data-testid="nav-viewer-column">
              {remotePreview && (
                <div style={{ ...navStyles.note, color: 'var(--accent, #4f46e5)' }} data-testid="nav-remote-preview-banner">
                  Previewing another document’s exact bytes: {remotePreview.label}.{' '}
                  <button type="button" style={navStyles.secondaryBtn} data-testid="nav-remote-preview-return" onClick={returnToSource}>
                    Return to revision {env.revision}
                  </button>
                </div>
              )}
              <PdfCanvasViewer
                url={viewerUrl}
                loading={remotePreview ? false : preview.loading}
                error={remotePreview ? null : preview.error}
                focus={remotePreview ? remotePreview.focus : focus}
                caption={viewerCaption}
                idPrefix="nav-pdf"
                selection={selectionReq}
                onSelect={handleViewerSelect}
                onSelectCancel={handleViewerSelectCancel}
                // A source / in-document-destination selection runs against this
                // document (the mediator returns any remote preview first); a
                // cross-document landing-point selection runs against the picker's
                // exact-bytes target preview. Both need selection enabled, so the
                // only gate is write permission + not mid-save.
                selectionEnabled={canMutate && !editorsBusy}
                selectionDisabledReason={mutationBlockedReason}
              />
              {preview.url && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <a
                    href={preview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ ...navStyles.secondaryBtn, textDecoration: 'none' }}
                    data-testid="nav-preview-open"
                  >
                    Open revision {env.revision} in a new tab
                  </a>
                  <a
                    href={preview.url}
                    download={`navigation-revision-${env.revision}.pdf`}
                    style={{ ...navStyles.secondaryBtn, textDecoration: 'none' }}
                    data-testid="nav-preview-download"
                  >
                    Download this revision
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const workspaceInline: CSSProperties = { marginBottom: 12 };
const workspaceOverlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  background: 'var(--surface, #fff)',
  padding: 16,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
};
const workspaceToolbar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 8,
  flexWrap: 'wrap',
};
const workspaceFilename: CSSProperties = {
  fontSize: 12,
  color: 'var(--text)',
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 360,
};
// Below this CONTAINER width the editor and preview stack instead of sitting
// side by side — two usable columns need roughly this much room, and a pinned
// pane can be far narrower than the viewport on a wide desktop.
const NAV_SIDE_BY_SIDE_MIN_WIDTH = 640;
const twoColumn: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 12,
  alignItems: 'start',
};
const twoColumnExpanded: CSSProperties = { ...twoColumn, flex: 1, minHeight: 0 };
// Narrow (stacked) layout: a single full-width column so the editor sits above
// a full-width preview. The viewer frame caps its own height and scrolls, so the
// PDF stays usable even when the pane is a quarter of the screen.
const oneColumn: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: 12,
  alignItems: 'start',
};
// Stacked INSIDE the expanded overlay: like `oneColumn` but claims the overlay's
// remaining height (`flex: 1; minHeight: 0`, as `twoColumnExpanded` does) so the
// editor + preview fill the modal rather than hugging the top on a narrow phone.
const oneColumnExpanded: CSSProperties = { ...oneColumn, flex: 1, minHeight: 0 };
const editorColumn: CSSProperties = { minWidth: 0, overflow: 'auto' };
const viewerColumn: CSSProperties = { minWidth: 0, position: 'sticky', top: 0 };
// Stacked: drop the sticky offset (the viewer is below the editor, not beside
// it) and reserve a minimum height so the preview never collapses to a sliver.
const viewerColumnStacked: CSSProperties = { minWidth: 0, minHeight: 320 };

export default NavigationTab;
